/**
 * StatusShapes — what a status looks like ON a body, in the pass-2 language.
 *
 *   hexShield  Walled Garden: a chunky faceted cel shell around the body with
 *              an ink silhouette, a hit-flash RIPPLE that runs over the shell
 *              from where it was struck, and an erode-out on expiry
 *   iceCrust   slowed: a jagged ice-crystal crust at the feet
 *   hazeRing   faded (your own body): a broken shimmer ring at the feet
 *
 * Pooled through CelShapes' pool; every per-instance value is a uniform.
 */

import * as THREE from 'three/webgpu';
import { Fn, uv, float, vec3, smoothstep, step, uniform, mix, atan, fract, time, sin, abs, normalView, normalLocal, positionLocal, dot, acos, normalize } from 'three/tsl';
import { cloud, erode } from './CelLook.js';
import { acquireShape, commonUniforms, ramp, band, baseMaterial, circleGeo } from './CelShapes.js';

let _shellGeo = null;
/** 80 flat facets. Non-indexed, so computeVertexNormals gives one normal per face. */
function shellGeo() {
  if (!_shellGeo) {
    _shellGeo = new THREE.IcosahedronGeometry(1, 1);
    _shellGeo.computeVertexNormals();
  }
  return _shellGeo;
}

/**
 * The shield. Facets shaded in three cel steps off a fixed light (top faces
 * hot, sides body, underside deep), the silhouette in INK (edge-on faces),
 * the bottom fading out so it never cuts the deck. `hitDir` (object space)
 * and `hit` (0 → 1) run a bright ripple band outward from the struck point,
 * with a whole-shell flash on the first frames. Erodes with a front.
 */
function createHexShield(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const hit = uniform(0);
  const hitDir = uniform(new THREE.Vector3(0, 0, 1));
  const c = ramp(u);

  const nl = dot(normalLocal, normalize(vec3(0.45, 0.85, 0.3)));
  const facing = normalView.z.abs();
  const rim = smoothstep(float(0.34), float(0.16), facing);
  const y = positionLocal.y;
  const foot = smoothstep(float(-0.95), float(-0.55), y);
  // Angular distance from the hit point, 0 at the point → 1 opposite.
  const ang = acos(dot(normalize(positionLocal), hitDir).clamp(-1, 1)).mul(1 / Math.PI);
  const live = step(float(0.001), hit);
  const ripple = band(ang.sub(hit.mul(1.1)), -0.07, 0.02, 0.02).mul(live);
  const flash = smoothstep(float(0.3), float(0.0), hit).mul(live);
  const n = cloud(uv().mul(3), u.seed.mul(5));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.14 });

  mat.colorNode = Fn(() => {
    let col = c.deep;
    col = mix(col, c.body, step(float(0.15), nl));
    col = mix(col, c.hot, step(float(0.82), nl));
    col = mix(col, c.hot, flash.mul(0.7));
    col = mix(col, c.ink, rim);
    col = mix(col, c.core, ripple);
    col = mix(col, c.core, e.front);
    return col;
  })();
  // Glassy: the body shows through the facets; the ink silhouette and the
  // ripple are the solid parts. The founder must see who is behind the wall.
  mat.opacityNode = Fn(() => {
    // The underside facets are firmer than the top ones, so the shell reads
    // as a chunky object with a lit crown rather than a pale bubble.
    const facet = mix(float(0.52), float(0.36), step(float(0.15), nl));
    const base = facet.add(rim.mul(0.7)).add(ripple.mul(0.6)).add(flash.mul(0.25));
    return base.min(1).mul(foot).mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(shellGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, hit, hitDir } };
}

/**
 * The ice crust: a crystal ring at the feet — seven jagged spikes out of a
 * ring, the feet showing through the middle, alternating facets, a white
 * keyline on the inner edge and an ink rim. A fixed ice blue.
 */
function createIceCrust(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y);
  const spikes = abs(sin(ang.mul(3.5).add(u.seed))).pow(6).mul(0.32).add(abs(sin(ang.mul(8).add(u.seed.mul(2)))).mul(0.08));
  const R = float(0.6).add(spikes);
  const q = r.div(R);
  const facet = step(float(0.5), fract(ang.mul(7 / (Math.PI * 2)).add(u.seed)));
  const n = cloud(local.mul(3), u.seed.mul(4));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(q, 0.46, 0.9, 0.03));
    col = mix(col, c.deep, facet.mul(band(q, 0.46, 0.9, 0.03)).mul(0.45));
    col = mix(col, c.hot, band(q, 0.5, 0.68, 0.03));
    col = mix(col, c.core, band(q, 0.52, 0.57, 0.02));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => band(q, 0.42, 1.0, 0.02).mul(e.alive).mul(u.fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * The haze ring: a ring at the feet broken by a scrolling noise field, so it
 * shimmers like heat over a road — the one cue a faded body leaves, for its
 * own owner. Identity colour, low alpha.
 */
function createHazeRing(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ring = band(r, 0.5, 0.95, 0.03);
  const n = cloud(local.mul(3).add(time.mul(0.9)), u.seed.mul(3));
  const alive = smoothstep(float(0.42), float(0.5), n);

  mat.colorNode = Fn(() => mix(c.body, c.hot, smoothstep(float(0.55), float(0.7), n)))();
  mat.opacityNode = Fn(() => ring.mul(alive).mul(0.45).mul(u.fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

export function hexShield(colorHex) {
  return acquireShape('hexShield', () => createHexShield(colorHex), colorHex);
}
export function iceCrust(colorHex) {
  return acquireShape('iceCrust', () => createIceCrust(colorHex), colorHex, { fixed: true });
}
export function hazeRing(colorHex) {
  return acquireShape('hazeRing', () => createHazeRing(colorHex), colorHex);
}

/** Every pooled key this module can produce, for the prewarm and its test. */
export const STATUS_SHAPE_KEYS = ['hexShield', 'iceCrust', 'hazeRing'];
